import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Card, CardContent, Grid,
  FormControl, InputLabel, Select, MenuItem, Chip, ToggleButton,
  ToggleButtonGroup, Autocomplete, Alert, Snackbar, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { format } from 'date-fns';
import { inventoryApi } from '../api/inventory.api';
import { productsApi } from '../api/products.api';
import { suppliersApi } from '../api/suppliers.api';
import { InventoryMovement, Product, Supplier } from '../types';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PaginatedTable from '../components/common/PaginatedTable';

const typeColors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'primary'> = {
  entrada: 'success',
  salida: 'error',
  ajuste: 'warning',
  ingreso_mercaderia: 'primary',
  venta: 'info',
};

const Inventory: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [movementType, setMovementType] = useState<string>('entrada');
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Ingreso mercadería
  const [ingresoProductId, setIngresoProductId] = useState<number | null>(null);
  const [ingresoSupplierId, setIngresoSupplierId] = useState<number>(0);
  const [expectedQty, setExpectedQty] = useState<number>(0);
  const [receivedQty, setReceivedQty] = useState<number>(0);
  const [unitCost, setUnitCost] = useState<number>(0);
  const [ingresoReason, setIngresoReason] = useState('');

  // Kardex
  const [kardexOpen, setKardexOpen] = useState(false);
  const [kardexData, setKardexData] = useState<any>(null);

  const loadData = async () => {
    try {
      const params: any = {};
      if (filterType) params.type = filterType;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const [movRes, prodRes, supRes] = await Promise.all([
        inventoryApi.getAll(params),
        productsApi.getAll(),
        suppliersApi.getAll(),
      ]);
      setMovements(movRes.data);
      setProducts(prodRes.data);
      setSuppliers(supRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [filterType, startDate, endDate]); // eslint-disable-line

  const selectedProduct = products.find((p) => p.id === selectedProductId);

  const handleSubmit = async () => {
    if (!selectedProductId || quantity <= 0) return;
    try {
      const data = { productId: selectedProductId, quantity, reason };
      if (movementType === 'entrada') {
        await inventoryApi.createEntrada(data);
      } else if (movementType === 'salida') {
        await inventoryApi.createSalida(data);
      } else {
        if (!reason.trim()) {
          setSnackbar({ open: true, message: 'El motivo es obligatorio para ajustes', severity: 'error' });
          return;
        }
        await inventoryApi.createAjuste(data);
      }
      setSnackbar({ open: true, message: 'Movimiento registrado exitosamente', severity: 'success' });
      setSelectedProductId(null); setQuantity(0); setReason('');
      loadData();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error al registrar movimiento',
        severity: 'error',
      });
    }
  };

  const handleIngresoMercaderia = async () => {
    if (!ingresoProductId || !ingresoSupplierId || receivedQty <= 0) return;
    try {
      await inventoryApi.createIngresoMercaderia({
        productId: ingresoProductId,
        supplierId: ingresoSupplierId,
        expectedQuantity: expectedQty,
        receivedQuantity: receivedQty,
        unitCost: unitCost || undefined,
        reason: ingresoReason || undefined,
      });
      setSnackbar({ open: true, message: 'Ingreso de mercaderia registrado', severity: 'success' });
      setIngresoProductId(null); setIngresoSupplierId(0);
      setExpectedQty(0); setReceivedQty(0); setUnitCost(0); setIngresoReason('');
      loadData();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error al registrar ingreso',
        severity: 'error',
      });
    }
  };

  const handleViewKardex = async (productId: number) => {
    try {
      const res = await inventoryApi.getKardex(productId);
      setKardexData(res.data);
      setKardexOpen(true);
    } catch (err) { console.error(err); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Movimientos de Inventario</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Movimiento Rapido" />
        <Tab label="Ingreso Mercaderia" />
        <Tab label="Historial" />
      </Tabs>

      {/* Tab 0: Quick Movement */}
      {tab === 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Registrar Movimiento</Typography>
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup value={movementType} exclusive
                onChange={(_, val) => val && setMovementType(val)} size="small">
                <ToggleButton value="entrada" sx={{ px: 3 }} color="success">Entrada</ToggleButton>
                <ToggleButton value="salida" sx={{ px: 3 }} color="error">Salida</ToggleButton>
                <ToggleButton value="ajuste" sx={{ px: 3 }} color="warning">Ajuste</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {movementType === 'ajuste' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Para ajustes, el motivo es obligatorio y se registra en auditoria.
              </Alert>
            )}
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete options={products}
                  getOptionLabel={(p) => `${p.code || ''} ${p.name} (${p.currentStock} ${p.unit})`}
                  value={products.find((p) => p.id === selectedProductId) || null}
                  onChange={(_, val) => setSelectedProductId(val?.id || null)}
                  renderInput={(params) => <TextField {...params} label="Producto" size="small" />}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <TextField fullWidth label="Cantidad" type="number" size="small"
                  value={quantity || ''}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                  inputProps={{ min: 0.01, step: 0.01 }} />
              </Grid>
              {selectedProduct && (
                <Grid size={{ xs: 6, sm: 2 }}>
                  <Chip label={`Stock: ${selectedProduct.currentStock} ${selectedProduct.unit}`}
                    color={selectedProduct.currentStock < selectedProduct.minStock ? 'error' : 'default'}
                    variant="outlined" />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField fullWidth
                  label={movementType === 'ajuste' ? 'Motivo (obligatorio)' : 'Razon / Notas'}
                  size="small" value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  required={movementType === 'ajuste'} />
              </Grid>
              <Grid size={{ xs: 12, sm: 1 }}>
                <Button variant="contained" fullWidth onClick={handleSubmit}
                  disabled={!selectedProductId || quantity <= 0}>
                  Registrar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Ingreso Mercadería */}
      {tab === 1 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Ingreso de Mercaderia con Proveedor</Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Registra el ingreso con cantidad esperada vs recibida para detectar diferencias.
            </Alert>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Autocomplete options={products}
                  getOptionLabel={(p) => `${p.code || ''} ${p.name}`}
                  value={products.find((p) => p.id === ingresoProductId) || null}
                  onChange={(_, val) => setIngresoProductId(val?.id || null)}
                  renderInput={(params) => <TextField {...params} label="Producto" />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Proveedor</InputLabel>
                  <Select value={ingresoSupplierId} label="Proveedor"
                    onChange={(e) => setIngresoSupplierId(Number(e.target.value))}>
                    <MenuItem value={0} disabled>Seleccionar</MenuItem>
                    {suppliers.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <TextField fullWidth label="Cantidad Esperada" type="number"
                  value={expectedQty || ''} onChange={(e) => setExpectedQty(Number(e.target.value))} />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <TextField fullWidth label="Cantidad Recibida" type="number"
                  value={receivedQty || ''} onChange={(e) => setReceivedQty(Number(e.target.value))} />
              </Grid>
              {expectedQty > 0 && receivedQty > 0 && expectedQty !== receivedQty && (
                <Grid size={12}>
                  <Alert severity="warning">
                    Diferencia detectada: {receivedQty - expectedQty} unidades
                    ({receivedQty > expectedQty ? 'exceso' : 'faltante'})
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth label="Costo Unitario" type="number"
                  value={unitCost || ''} onChange={(e) => setUnitCost(Number(e.target.value))} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField fullWidth label="Costo Total" disabled
                  value={unitCost && receivedQty ? (unitCost * receivedQty).toFixed(2) : '0.00'} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth label="Referencia (factura, guia)" value={ingresoReason}
                  onChange={(e) => setIngresoReason(e.target.value)} />
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Button variant="contained" color="primary" fullWidth sx={{ height: '100%' }}
                  onClick={handleIngresoMercaderia}
                  disabled={!ingresoProductId || !ingresoSupplierId || receivedQty <= 0}>
                  Registrar Ingreso
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tab 2: History (also shown on tab 0) */}
      {(tab === 0 || tab === 2) && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={filterType} label="Tipo" onChange={(e) => setFilterType(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="entrada">Entrada</MenuItem>
                <MenuItem value="salida">Salida</MenuItem>
                <MenuItem value="ajuste">Ajuste</MenuItem>
                <MenuItem value="ingreso_mercaderia">Ingreso Mercaderia</MenuItem>
                <MenuItem value="venta">Venta</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" label="Desde" type="date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" label="Hasta" type="date" value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} />
          </Box>

          <PaginatedTable
            columns={[
              { id: 'createdAt', label: 'Fecha', format: (v: any) => format(new Date(v), 'dd/MM/yyyy HH:mm') },
              { id: 'product', label: 'Producto', format: (_: any, row: any) => <span style={{ fontWeight: 500 }}>{row.product?.name || '-'}</span> },
              { id: 'type', label: 'Tipo', format: (v: any) => <Chip label={v.replace('_', ' ')} size="small" color={typeColors[v] || 'default'} /> },
              { id: 'quantity', label: 'Cantidad', align: 'right' },
              { id: 'previousStock', label: 'Stock Ant.', align: 'right' },
              { id: 'newStock', label: 'Stock Nuevo', align: 'right' },
              { id: 'user', label: 'Usuario', format: (_: any, row: any) => row.user?.fullName || '-' },
              { id: 'reason', label: 'Razón', format: (v: any) => v || '-' },
            ]}
            rows={movements}
            exportFileName="movimientos"
            searchFields={['reason']}
            emptyMessage="No hay movimientos registrados"
            renderActions={(m) => (
              <Button size="small" onClick={() => handleViewKardex(m.productId)}>
                Kardex
              </Button>
            )}
          />
        </>
      )}

      {/* Kardex Dialog */}
      <Dialog open={kardexOpen} onClose={() => setKardexOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Kardex - {kardexData?.product?.name} ({kardexData?.product?.code})
        </DialogTitle>
        <DialogContent>
          {kardexData && (
            <>
              <Box sx={{ mb: 2 }}>
                <Chip label={`Stock actual: ${kardexData.product.currentStock} ${kardexData.product.unit}`}
                  color="primary" />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell align="right">Cantidad</TableCell>
                      <TableCell align="right">Stock Ant.</TableCell>
                      <TableCell align="right">Stock Nuevo</TableCell>
                      <TableCell>Razon</TableCell>
                      <TableCell>Usuario</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kardexData.movements.map((m: any) => (
                      <TableRow key={m.id}>
                        <TableCell>{format(new Date(m.createdAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                        <TableCell>
                          <Chip label={m.type.replace('_', ' ')} size="small"
                            color={typeColors[m.type] || 'default'} />
                        </TableCell>
                        <TableCell align="right">{m.quantity}</TableCell>
                        <TableCell align="right">{m.previousStock}</TableCell>
                        <TableCell align="right">{m.newStock}</TableCell>
                        <TableCell>{m.reason || '-'}</TableCell>
                        <TableCell>{m.user?.fullName || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKardexOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Inventory;
